import {
  TasksValueObjectsTaskCategoryEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'

import { getTaskCategoryEnumValues, getTaskKinds } from './constants'

type DateRange = {
  from: Date
  to: Date
}

type FilterValue = unknown

interface Filters {
  category?: FilterValue
  affecteeSelection?: FilterValue
  createdAt?: FilterValue
}

interface Sorting {
  field?: string
  order?: 'asc' | 'desc'
}

interface Pagination {
  cursor?: string | number | null
}

interface MapVariablesParams {
  filters: Filters
  sortings?: Sorting[]
  pagination?: Pagination
}

interface MapVariablesOptions {
  companyId: number
  completed: boolean
  manualTasksEnabled: boolean
}

/**
 * Type guard to check if a string is a valid TaskCategory
 */
export const isTaskCategory = (category?: string): category is TasksValueObjectsTaskCategoryEnum =>
  !!category && getTaskCategoryEnumValues().some((v) => v === category)

/**
 * Parse category filter values into valid TaskCategory array
 */
export const parseCategories = (
  categoryValue: FilterValue
): TasksValueObjectsTaskCategoryEnum[] => {
  const categoryValues = Array.isArray(categoryValue) ? categoryValue : []
  return categoryValues.reduce<TasksValueObjectsTaskCategoryEnum[]>((acc, c) => {
    if (c && typeof c === 'string' && isTaskCategory(c)) {
      acc.push(c)
    }
    return acc
  }, [])
}

/**
 * Parse affectee selection filter into employee selector input
 */
export const parseAffecteeSelection = (
  affecteeSelectionValue: FilterValue
): { ids: number[] } | undefined => {
  if (!Array.isArray(affecteeSelectionValue) || affecteeSelectionValue.length === 0) {
    return undefined
  }
  const ids = affecteeSelectionValue
    .map((id) => (typeof id === 'number' ? id : Number(id)))
    .filter((id): id is number => Number.isFinite(id))

  if (ids.length === 0) {
    return undefined
  }

  return { ids }
}

/**
 * Type guard to check if a value is a valid DateRange
 */
export const isDateRange = (value: unknown): value is DateRange =>
  !!value &&
  typeof value === 'object' &&
  'from' in value &&
  value.from instanceof Date &&
  'to' in value &&
  value.to instanceof Date

/**
 * Convert a Date to ISO string at start of day (00:00:00)
 */
export const toStartOfDayISO = (date: Date): string => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).toISOString()
}

/**
 * Convert a Date to ISO string at end of day (23:59:59.999)
 */
export const toEndOfDayISO = (date: Date): string => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).toISOString()
}

/**
 * Parse createdAt date range filter into separate from/to values
 */
export const parseCreatedAtFilter = (
  createdAtValue: FilterValue
): { createdAtFrom: string; createdAtTo: string } | undefined => {
  if (!isDateRange(createdAtValue)) {
    return undefined
  }

  return {
    createdAtFrom: toStartOfDayISO(createdAtValue.from),
    createdAtTo: toEndOfDayISO(createdAtValue.to),
  }
}

/**
 * Map DataCollection filter/sorting/pagination state to GraphQL query variables
 */
export const createMapVariables = (options: MapVariablesOptions) => {
  const { companyId, completed, manualTasksEnabled } = options

  return ({ filters, sortings, pagination }: MapVariablesParams) => {
    const sortingByCreationDate = sortings?.[0]?.field === 'creationDateGroup'
    const sortingDirection = sortings?.[0]?.order

    const isFirstPage = !pagination?.cursor

    const categories = parseCategories(filters.category)
    const affecteeSelection = parseAffecteeSelection(filters.affecteeSelection)
    const createdAtFilter = parseCreatedAtFilter(filters.createdAt)

    const commonVariables = {
      affecteeSelection,
      categories,
      companyId,
      createdAtFrom: createdAtFilter?.createdAtFrom,
      createdAtTo: createdAtFilter?.createdAtTo,
      kinds: manualTasksEnabled ? undefined : getTaskKinds(),
      includeCategoryCount: isFirstPage,
      includeGroupingCount: false,
    }

    if (completed) {
      const statuses: TasksValueObjectsTaskStatusEnum[] = ['done']
      return {
        ...commonVariables,
        statuses,
        sortOrder: {
          field: 'completedAt',
          order: 'desc',
        },
      }
    }

    const statuses: TasksValueObjectsTaskStatusEnum[] = ['todo', 'in_progress']

    return {
      ...commonVariables,
      statuses,
      sortOrder: {
        field: sortingByCreationDate ? ('created_at' as const) : ('due_on' as const),
        order: sortingDirection ?? 'asc',
        nullsLast: !sortingByCreationDate,
      },
    }
  }
}
